/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: LogViewerPage.tsx
 ??? Created: 2025-12-10
 ?? Description:
    Log Viewer Page - View FreeSWITCH and Fail2ban logs
 ----------------------------------------------------------------*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    FileText,
    RefreshCw,
    Search,
    Download,
    Trash2,
    Filter,
    ChevronDown,
    AlertCircle,
    CheckCircle,
    XCircle,
    Info,
    AlertTriangle,
    Bug,
    Server,
    Shield,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useNotifications } from '../../contexts/NotificationContext';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { SimpleTooltip } from '../../components/ui/SimpleTooltip';
import {
    LogType,
    LogEntry,
    LogViewerParams,
    getLogEntries,
    getLogFilesStatus,
    clearLog,
    getLogDownloadUrl,
    LogFileStatus,
} from './api';
import { useSidebarState } from '../../hooks/useSidebarState';

// Log level colors
const LEVEL_COLORS: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
    DEBUG: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-600 dark:text-gray-400', icon: <Bug className="h-3 w-3" /> },
    INFO: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400', icon: <Info className="h-3 w-3" /> },
    NOTICE: { bg: 'bg-cyan-100 dark:bg-cyan-900/30', text: 'text-cyan-600 dark:text-cyan-400', icon: <Info className="h-3 w-3" /> },
    WARNING: { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-600 dark:text-yellow-400', icon: <AlertTriangle className="h-3 w-3" /> },
    ERR: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400', icon: <XCircle className="h-3 w-3" /> },
    ERROR: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400', icon: <XCircle className="h-3 w-3" /> },
    CRIT: { bg: 'bg-red-200 dark:bg-red-900/50', text: 'text-red-700 dark:text-red-300', icon: <AlertCircle className="h-3 w-3" /> },
    ALERT: { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-600 dark:text-orange-400', icon: <AlertCircle className="h-3 w-3" /> },
};

const LOG_LEVELS = ['ALL', 'DEBUG', 'INFO', 'NOTICE', 'WARNING', 'ERR', 'CRIT'];

export default function LogViewerPage() {
    const { t } = useTranslation('logViewer');
    const { showSuccess, showError } = useNotifications();

    // State
    const [selectedLog, setSelectedLog] = useState<LogType>('freeswitch');
    const [entries, setEntries] = useState<LogEntry[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [levelFilter, setLevelFilter] = useState('ALL');
    const [lineCount, setLineCount] = useState(200);
    const [totalLines, setTotalLines] = useState(0);
    const [autoRefresh, setAutoRefresh] = useState(false);
    const [showClearModal, setShowClearModal] = useState(false);
    const [isClearing, setIsClearing] = useState(false);
    const [logStatus, setLogStatus] = useState<{ freeswitch: LogFileStatus; fail2ban: LogFileStatus } | null>(null);
    const [showLevelDropdown, setShowLevelDropdown] = useState(false);

    const logContainerRef = useRef<HTMLDivElement>(null);
    const autoRefreshIntervalRef = useRef<number | null>(null);

    const { width: sidebarWidth } = useSidebarState();
    const effectiveSidebarWidth = sidebarWidth === 0 ? 350 : sidebarWidth;

    // Check if user is scrolled to bottom
    const isUserAtBottom = useCallback(() => {
        if (!logContainerRef.current) return true;
        const { scrollTop, scrollHeight, clientHeight } = logContainerRef.current;
        // Tolerance
        return scrollHeight - scrollTop - clientHeight < 50;
    }, []);

    // Load log status
    const loadLogStatus = useCallback(async () => {
        try {
            const response = await getLogFilesStatus();
            if (response.success) {
                setLogStatus(response.data);
            }
        } catch (error) {
            console.error('Failed to load log status:', error);
        }
    }, []);

    // Load log entries
    const loadLogs = useCallback(async (scrollToBottom = true) => {
        setIsLoading(true);
        try {
            const params: LogViewerParams = {
                type: selectedLog,
                lines: lineCount,
                tail: true,
            };

            if (searchQuery.trim()) {
                params.search = searchQuery.trim();
            }

            if (levelFilter !== 'ALL') {
                params.level = levelFilter;
            }

            const response = await getLogEntries(params);
            if (response.success) {
                setEntries(response.data.entries);
                setTotalLines(response.data.totalLines);

                // Scroll to bottom if requested
                if (scrollToBottom && logContainerRef.current) {
                    setTimeout(() => {
                        if (logContainerRef.current) {
                            logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
                        }
                    }, 100);
                }
            }
        } catch (error) {
            showError(error.message || t('loadError', 'Failed to load logs'));
            setEntries([]);
        } finally {
            setIsLoading(false);
        }
    }, [selectedLog, lineCount, searchQuery, levelFilter, showError, t]);

    // Initial load
    useEffect(() => {
        loadLogStatus();
        loadLogs(true);
    }, [selectedLog]);

    // Auto refresh
    useEffect(() => {
        if (autoRefresh) {
            autoRefreshIntervalRef.current = window.setInterval(() => {
                const shouldScroll = isUserAtBottom();
                loadLogs(shouldScroll);
            }, 5000);
        } else {
            if (autoRefreshIntervalRef.current) {
                window.clearInterval(autoRefreshIntervalRef.current);
                autoRefreshIntervalRef.current = null;
            }
        }

        return () => {
            if (autoRefreshIntervalRef.current) {
                window.clearInterval(autoRefreshIntervalRef.current);
            }
        };
    }, [autoRefresh, loadLogs, isUserAtBottom]);

    // Handle clear log
    const handleClearLog = async () => {
        setIsClearing(true);
        try {
            const response = await clearLog(selectedLog);
            if (response.success) {
                showSuccess(t('clearSuccess', 'Log cleared successfully'));
                setEntries([]);
                loadLogs();
            } else {
                showError(response.message || t('clearError', 'Failed to clear log'));
            }
        } catch (error) {
            showError(error.message || t('clearError', 'Failed to clear log'));
        } finally {
            setIsClearing(false);
            setShowClearModal(false);
        }
    };

    // Handle download
    const handleDownload = () => {
        window.open(getLogDownloadUrl(selectedLog), '_blank');
    };

    // Format file size
    const formatFileSize = (bytes?: number): string => {
        if (!bytes) return 'N/A';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    // Get level style
    const getLevelStyle = (level?: string) => {
        if (!level) return LEVEL_COLORS.INFO;
        const upperLevel = level.toUpperCase();
        return LEVEL_COLORS[upperLevel] || LEVEL_COLORS.INFO;
    };

    const currentLogStatus = logStatus ? logStatus[selectedLog] : null;

    return (
        <div className="p-6 pb-0"> {/* Add horizontal padding for spacer context, but remove bottom padding */}

            {/* Fixed Header - Full Width */}
            <div
                className="fixed top-[80px] z-40 bg-card/95 backdrop-blur-md shadow-sm border-b border-border/50 p-4 flex flex-col gap-4"
                style={{
                    left: `${effectiveSidebarWidth}px`, // Align to sidebar
                    right: '0px'  // Full width to right edge
                }}
            >
                {/* Title and Actions */}
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <FileText className="h-6 w-6 text-primary" />
                        <h1 className="text-2xl font-semibold text-foreground">
                            {t('title', 'Log File Viewer')}
                        </h1>
                    </div>

                    <div className="flex gap-2">
                        <SimpleTooltip content={autoRefresh ? t('stopAutoRefresh', 'Stop Auto-refresh') : t('startAutoRefresh', 'Auto-refresh (5s)')}>
                            <Button
                                variant={autoRefresh ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setAutoRefresh(!autoRefresh)}
                            >
                                <RefreshCw className={`h-4 w-4 ${autoRefresh ? 'animate-spin' : ''}`} />
                            </Button>
                        </SimpleTooltip>
                        <SimpleTooltip content={t('refresh', 'Refresh')}>
                            <Button variant="outline" size="sm" onClick={() => loadLogs()} disabled={isLoading}>
                                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                            </Button>
                        </SimpleTooltip>
                        <SimpleTooltip content={t('download', 'Download')}>
                            <Button variant="outline" size="sm" onClick={handleDownload}>
                                <Download className="h-4 w-4" />
                            </Button>
                        </SimpleTooltip>
                        <SimpleTooltip content={t('clear', 'Clear Log')}>
                            <Button variant="outline" size="sm" onClick={() => setShowClearModal(true)} className="text-red-600 hover:text-red-700">
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </SimpleTooltip>
                    </div>
                </div>

                {/* Controls Bar */}
                <div className="flex flex-col xl:flex-row gap-4 items-start xl:items-center justify-between">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full xl:w-auto">
                        <div className="flex gap-2 bg-muted p-1 rounded-lg">
                            <button
                                onClick={() => setSelectedLog('freeswitch')}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${selectedLog === 'freeswitch'
                                    ? 'bg-background text-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                                    }`}
                            >
                                <Server className="h-4 w-4" />
                                FreeSWITCH
                            </button>
                            <button
                                onClick={() => setSelectedLog('fail2ban')}
                                className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${selectedLog === 'fail2ban'
                                    ? 'bg-background text-foreground shadow-sm'
                                    : 'text-muted-foreground hover:text-foreground'
                                    }`}
                            >
                                <Shield className="h-4 w-4" />
                                Fail2ban
                            </button>
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 w-full xl:w-auto justify-end">
                        <div className="flex-1 min-w-[200px] xl:max-w-[300px]">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && loadLogs()}
                                    placeholder={t('searchPlaceholder', 'Search logs...')}
                                    className="pl-9 h-9 bg-background"
                                />
                            </div>
                        </div>

                        <div className="relative">
                            <button
                                onClick={() => setShowLevelDropdown(!showLevelDropdown)}
                                className="flex items-center gap-2 px-3 py-2 border border-border rounded-lg bg-background hover:bg-muted transition-colors h-9 text-sm min-w-[120px] justify-between"
                            >
                                <div className="flex items-center gap-2">
                                    <Filter className="h-4 w-4 text-muted-foreground" />
                                    <span>{levelFilter === 'ALL' ? t('allLevels', 'All Levels') : levelFilter}</span>
                                </div>
                                <ChevronDown className="h-4 w-4 opacity-50" />
                            </button>
                            {showLevelDropdown && (
                                <div className="absolute top-full right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-10 min-w-[150px] overflow-hidden">
                                    {LOG_LEVELS.map((level) => (
                                        <button
                                            key={level}
                                            onClick={() => {
                                                setLevelFilter(level);
                                                setShowLevelDropdown(false);
                                            }}
                                            className={`w-full text-left px-3 py-2 hover:bg-muted transition-colors text-sm flex items-center gap-2 ${levelFilter === level ? 'bg-primary/10 text-primary' : ''
                                                }`}
                                        >
                                            {level === 'ALL' ? <Filter className="h-3 w-3" /> : getLevelStyle(level).icon}
                                            {level === 'ALL' ? t('allLevels', 'All Levels') : level}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        <select
                            value={lineCount}
                            onChange={(e) => setLineCount(parseInt(e.target.value))}
                            className="px-3 py-2 border border-border rounded-lg bg-background text-sm h-9 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none"
                        >
                            <option value={100}>100 lines</option>
                            <option value={200}>200 lines</option>
                            <option value={500}>500 lines</option>
                            <option value={1000}>1000 lines</option>
                            <option value={2000}>2000 lines</option>
                        </select>

                        <Button onClick={() => loadLogs()} disabled={isLoading} size="sm" className="h-9 px-4">
                            {t('applyFilters', 'Apply')}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Spacer for fixed header - Reduced height */}
            <div className="h-[120px]"></div>

            {/* Log Content - Fixed position locked */}
            <div
                ref={logContainerRef}
                className="fixed right-0 bg-gray-900 border-t border-border overflow-auto font-mono text-sm shadow-inner z-0"
                style={{
                    top: '200px', // Raised up
                    bottom: '36px', // Space for footer
                    left: `${effectiveSidebarWidth}px`,
                }}
            >
                {isLoading && entries.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-gray-400 gap-3">
                        <RefreshCw className="h-6 w-6 animate-spin" />
                        <span className="text-lg">{t('loading', 'Loading logs...')}</span>
                    </div>
                ) : entries.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
                        <FileText className="h-10 w-10 opacity-20" />
                        <span>{t('noEntries', 'No log entries found')}</span>
                        <Button variant="ghost" onClick={() => loadLogs()} className="text-primary">
                            {t('tryRefreshing', 'Try refreshing')}
                        </Button>
                    </div>
                ) : (
                    <table className="w-full">
                        <tbody className="divide-y divide-gray-800">
                            {entries.map((entry, index) => {
                                const levelStyle = getLevelStyle(entry.level);
                                return (
                                    <tr
                                        key={index}
                                        className="hover:bg-gray-800/50 transition-colors group"
                                    >
                                        <td className="px-3 py-1.5 text-gray-600 text-right w-14 select-none text-xs border-r border-gray-800 bg-gray-900/50">
                                            {entry.lineNumber}
                                        </td>
                                        {entry.timestamp && (
                                            <td className="px-3 py-1.5 text-blue-400/80 whitespace-nowrap w-48 text-xs font-medium">
                                                {entry.timestamp}
                                            </td>
                                        )}
                                        {entry.level && (
                                            <td className="px-3 py-1.5 w-24 align-top">
                                                <span
                                                    className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] uppercase font-bold tracking-wider ${levelStyle.bg} ${levelStyle.text}`}
                                                >
                                                    {levelStyle.icon}
                                                    {entry.level}
                                                </span>
                                            </td>
                                        )}
                                        <td className="px-3 py-1.5 text-gray-300 break-all leading-6 group-hover:text-white transition-colors">
                                            {entry.content}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Fixed Footer Bar */}
            <div
                className="fixed bottom-0 right-0 h-[36px] bg-card border-t border-border flex items-center px-4 justify-between text-xs z-50"
                style={{ left: `${effectiveSidebarWidth}px` }}
            >
                <div className="flex items-center gap-4">
                    {currentLogStatus && (
                        <>
                            <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">{t('status', 'Status')}:</span>
                                <span className={`flex items-center gap-1.5 font-medium ${currentLogStatus.available ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                                    {currentLogStatus.available ? (
                                        <CheckCircle className="h-3 w-3" />
                                    ) : (
                                        <XCircle className="h-3 w-3" />
                                    )}
                                    {currentLogStatus.available ? t('available', 'Online') : t('unavailable', 'Offline')}
                                </span>
                            </div>
                            <span className="w-px h-3 bg-border"></span>
                            <div className="flex items-center gap-2">
                                <span className="text-muted-foreground">{t('size', 'Size')}:</span>
                                <span className="font-mono text-foreground font-medium">
                                    {currentLogStatus.size !== undefined ? formatFileSize(currentLogStatus.size) : 'N/A'}
                                </span>
                            </div>
                        </>
                    )}
                </div>

                <div className="flex items-center gap-4 text-muted-foreground">
                    <div className="flex items-center gap-2">
                        <span>{t('totalLines', 'Total Lines')}:</span>
                        <span className="font-mono text-foreground font-medium">{totalLines.toLocaleString()}</span>
                    </div>
                </div>
            </div>

            {/* Clear Confirmation Modal */}
            <ConfirmationModal
                isOpen={showClearModal}
                onClose={() => setShowClearModal(false)}
                onConfirm={handleClearLog}
                title={t('clearTitle', 'Clear Log File')}
                message={t('clearConfirm', `Are you sure you want to clear the ${selectedLog} log file? This action cannot be undone.`)}
                confirmText={t('common:clear', 'Clear')}
                cancelText={t('common:cancel', 'Cancel')}
                type="danger"
                isLoading={isClearing}
            />
        </div>
    );
}
